<template>
  <v-card outlined class="outlined mb-6 elevation-0 rounded-lg">
    <v-toolbar color="transparent" flat class="text-h5 mb-2 rounded-0 py-1">
      <v-layout justify-space-between align-center class="mt-4">
        <v-flex>Available Units</v-flex>
        <v-flex>
          <v-layout justify-end align-center>
            <v-autocomplete
              data-testid="facility-available-units-filter-menu"
              class="facility-available-units-filter-menu pt-3 pb-2 mr-4"
              v-model="selectedFeature"
              v-show="searchOn"
              align-with-title
              :items="availableFeatures"
              label="Filter by available features..."
              :hint="
                !selectedFeature
                  ? 'Enter or select a feature'
                  : 'Click X to reset the list'
              "
              persistent-hint
              clearable
            >
              <template slot="item" slot-scope="data">
                <v-list-item-content>
                  {{ data.item }}
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-btn
              data-testid="facility-available-units-search-btn"
              class="facility-available-units-search-btn"
              icon
              @click="toggleSearch"
            >
              <v-icon>search</v-icon>
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="filteredUnits"
      :loading="loading"
      hide-default-footer
      disable-pagination
      no-data-text="There are no units to display"
      class="mt-4"
    >
      <template slot="item" slot-scope="props">
        <tr>
          <td class="py-2">
            <div class="text-body-2 font-weight-bold">
              <span v-if="props.item.width">
                {{ props.item.width }}' x {{ props.item.length }}' -
                {{ props.item.type_name }}
              </span>
              <span v-else
                >{{ props.item.length }}' - {{ props.item.type_name }}</span
              >
            </div>
            <span class="text-caption">{{
              props.item.features.join(", ")
            }}</span>
          </td>
          <td class="py-1">
            <span v-if="props.item.width">{{ props.item.area }} sq. ft.</span>
            <span v-else>N/A</span>
          </td>
          <td class="py-1">{{ props.item.promotion_text }}</td>
          <td class="py-1">${{ props.item.rate.toFixed(2) }}</td>
          <td class="py-1">{{ props.item.product_type }}</td>
          <td class="py-1">{{ props.item.total_vacant }}</td>
          <td class="py-1">
            <v-layout column>
              <v-btn
                data-testid="facility-new-lead-reservation-btn"
                class="facility-new-lead-reservation-btn my-1 info"
                small
                @click.stop="openReserveUnitModal(props.item)"
              >
                New Lead/Reservation
              </v-btn>
              <v-btn
                data-testid="facility-rent-unit-btn"
                class="facility-rent-unit-btn my-1 primary"
                small
                @click.stop="openRentUnitModal(props.item)"
              >
                Rent Unit
              </v-btn>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
    <rent-unit
      :rentUnitModal="rentUnitModal"
      :tpp="tpp"
      :initialCharges="initialCharges"
      :rentUnitStep="rentUnitStep"
      @currentStep="setRentUnitStep($event)"
      @collapse="closeRentUnitModal"
    />
    <reserve-unit
      :reserveUnitModal="reserveUnitModal"
      @collapse="closeReserveUnitModal"
      @reservationCreated="reservationCreated"
      :reservedUnit="reservedUnit"
    />
  </v-card>
</template>

<script>
/* eslint-disable eqeqeq */
import { emailRules, inputRules, updatePhoneRules } from "@/helpers/form-rules";
import RentUnit from "@/components/rent-unit/RentUnit";
import ReserveUnit from "@/components/reserve-unit/ReserveUnit";
import { facilityUnitsHeaders } from "@/helpers/facilities/facility-units-data-table-headers";
import moment from "moment";

export default {
  props: {
    availableUnits: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      loading: false,

      rentUnitStep: 0,

      rentUnitModal: false,
      reserveUnitModal: false,

      reservedUnit: "",
      tenantId: null,

      inputRules: inputRules,
      emailRules: emailRules,
      phoneRules: updatePhoneRules,

      unit: "",
      tpp: [],
      initialCharges: [],

      selectedFeature: "",
      searchOn: false,

      headers: facilityUnitsHeaders
    };
  },

  methods: {
    openRentUnitModal(unit) {
      let payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: unit.unit_id_first_available,
        concession_id: unit.concession_id,
        move_in_date: moment().format("MM/DD/YYYY"),
        waiting_id: unit.waiting_list_id
      };
      this.$store.commit("updatePromotion", unit.concession_id);
      this.$store.dispatch("getMerchandise", this.$route.params.facilityId);
      this.$store.dispatch(
        "getMoveInInstructions",
        this.$route.params.facilityId
      );
      this.$store.dispatch("getSimilarUnits", payload);
      this.$store.dispatch("handleQuotedRate", unit);
      this.$store.commit("setUnit", unit);
      this.getUnitFeatures(payload);
      this.getMoveInCost({
        facilityId: this.$route.params.facilityId,
        external_promotion_id: unit.concession_id,
        move_in_date: moment().format("MM/DD/YYYY"),
        external_reservation_id: unit.waiting_list_id
      });
      this.getPromotionOptions(payload);
      this.getTenantProtectionPlans();
      this.rentUnitModal = true;
      this.rentUnitStep = 1;
    },

    closeRentUnitModal() {
      this.rentUnitModal = false;
      this.$store.commit("updateInfo", null); // tenant info
      this.$store.commit("setDeferPromotion", false);
    },

    openReserveUnitModal(unit) {
      this.reservedUnit = unit;
      this.$store.commit("updateInfo", null); // tenant info
      this.reserveUnitModal = true;
    },

    closeReserveUnitModal() {
      this.reserveUnitModal = false;
      this.reservedUnit = "";
      this.$store.commit("updateInfo", null); // tenant info
    },

    reservationCreated() {
      this.closeReserveUnitModal();
      this.$emit("reservationCreated");
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    getTenantProtectionPlans() {
      this.$store
        .dispatch("getTenantProtectionPlans", this.$route.params.facilityId)
        .then((resp) => {
          this.tpp = resp.data;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
        });
    },

    getMoveInCost(payload) {
      this.$store
        .dispatch("getMoveInCost", payload)
        .catch((err) => {
          this.$store.dispatch("handleError", err);
        });
    },

    getUnitFeatures(payload) {
      this.$store
        .dispatch("getUnitFeatures", payload)
        .then((resp) => {
          this.unitFeatures = resp.data;
        })
        .catch((err) => {
          this.$store.dispatch("handleError", err);
        });
    },

    getPromotionOptions(payload) {
      this.$store.dispatch("getPromotionOptions", payload);
    },

    toggleSearch() {
      if (this.searchOn === true) {
        this.searchOn = false;
      } else {
        this.searchOn = true;
      }
    },

    setRentUnitStep(step) {
      this.rentUnitStep = step;
    }
  },

  computed: {
    availableFeatures() {
      let arr = [];
      this.availableUnits.map((unit) => {
        for (let detail in unit) {
          if (detail == "features") {
            Object.values(unit[detail]).forEach((value) => arr.push(value));
          }
        }
      });
      return [...new Set(arr)];
    },

    filteredUnits() {
      return this.selectedFeature
        ? this.availableUnits.filter((unit) => {
          for (let detail in unit) {
            if (
              detail == "features" &&
              Object.values(unit[detail]).includes(this.selectedFeature)
            ) {
              return unit[detail];
            }
          }
        })
        : this.availableUnits;
    }
  },

  components: {
    RentUnit,
    ReserveUnit
  }
};
</script>

<style>
</style>
